import { FilterableApiStatus, useGetTransportOrderExportMutation } from '../../../api/transportOrderApi';
import { useState } from 'react';
import { LocationFilter } from '../../../sharedComponents/filter/LocationFilter';
import { TableCategory } from '../../tableCategory.types';
import { ExportModal } from '../../../sharedComponents/export/ExportModal';
import { CategoryFilter } from '../../../sharedComponents/export/CategoryFilter';
import { DateRangeFilter } from '../../../sharedComponents/filter/DateRangeFilter';
import { DateInput, validateDateTimeFields } from '../../../sharedComponents/dateHelper';
import { getValidDate } from '../../../sharedComponents/inputValidations';

const FALL_BACK_FILE_NAME = 'transport-order-export.xlsx';

export type TransportAssignmentsExportFilters = {
    tableCategories: TableCategory[];
    fromLocation?: string;
    toLocation?: string;
    startDate: DateInput;
    endDate: DateInput;
};

export const DEFAULT_FILTERS: TransportAssignmentsExportFilters = {
    tableCategories: [TableCategory.INBOX, TableCategory.EN_ROUTE, TableCategory.FINISHED],
    fromLocation: undefined,
    toLocation: undefined,
    startDate: undefined,
    endDate: undefined,
};

export const tableCategoryCheckboxDefinition: { category: TableCategory; labelId: string }[] = [
    { category: TableCategory.INBOX, labelId: 'outboundPortal.transportAssignments.table.toolbar.inbox' },
    { category: TableCategory.EN_ROUTE, labelId: 'outboundPortal.transportAssignments.table.toolbar.enRoute' },
    { category: TableCategory.FINISHED, labelId: 'outboundPortal.transportAssignments.table.toolbar.finished' },
    { category: TableCategory.ARCHIVE, labelId: 'outboundOrderBook.common.table.toolbar.archive' },
];

const getStatusesForCategory = (categories: TableCategory[]) => {
    const statuses = categories.flatMap((category) => {
        switch (category) {
            case TableCategory.INBOX:
                return [
                    FilterableApiStatus.UNCONFIRMED,
                    FilterableApiStatus.CONFIRMED,
                    FilterableApiStatus.CANCELLATION,
                ];
            case TableCategory.EN_ROUTE:
                return [FilterableApiStatus.CANCELLATION, FilterableApiStatus.LOADED];
            case TableCategory.FINISHED:
                return [FilterableApiStatus.CANCELLATION, FilterableApiStatus.UNLOADED];
            case TableCategory.ARCHIVE:
                return [
                    FilterableApiStatus.UNCONFIRMED,
                    FilterableApiStatus.CONFIRMED,
                    FilterableApiStatus.LOADED,
                    FilterableApiStatus.UNLOADED,
                    FilterableApiStatus.CANCELLATION,
                ];
        }
    });
    return [...new Set(statuses)];
};

const getArchived = (categories: TableCategory[]): boolean | undefined =>
    !categories.includes(TableCategory.ARCHIVE) ? false : categories.length === 1 ? true : undefined;

export const TransportAssignmentExport = () => {
    const [localFilters, setLocalFilters] = useState(DEFAULT_FILTERS);
    const [getExcel] = useGetTransportOrderExportMutation();
    const { hasStartDateTimeError, hasEndDateTimeError, hasTimeRangeOrderError } = validateDateTimeFields(
        localFilters.startDate,
        localFilters.endDate,
    );

    const resetFilters = () => setLocalFilters(DEFAULT_FILTERS);
    const setTableCategories = (categories: TableCategory[]) => {
        setLocalFilters((prevState) => ({
            ...prevState,
            tableCategories: categories,
        }));
    };

    const body = (
        <>
            <CategoryFilter
                categories={localFilters.tableCategories}
                categoryDefinition={tableCategoryCheckboxDefinition}
                setCategories={setTableCategories}
            />
            <div className="margin-top-20">
                <DateRangeFilter
                    timeframeFilterTitle="outboundOrderBook.transportAssignments.filter.orderDateRange"
                    startDateFilterName="startDate"
                    endDateFilterName="endDate"
                    startDate={localFilters.startDate}
                    endDate={localFilters.endDate}
                    setLocalFilters={setLocalFilters}
                />
            </div>
            <div className="display-flex gap-20">
                <div className="flex-basis-100pct">
                    <LocationFilter
                        headerMessageId="outboundOrderBook.transportAssignments.filter.pickUpLocation"
                        value={localFilters.fromLocation}
                        onLocationChange={(fromLocation) => {
                            setLocalFilters((prevState) => ({ ...prevState, fromLocation }));
                        }}
                    />
                </div>
                <div className="flex-basis-100pct">
                    <LocationFilter
                        headerMessageId="outboundOrderBook.transportAssignments.filter.deliveryLocation"
                        value={localFilters.toLocation}
                        onLocationChange={(toLocation) => {
                            setLocalFilters((prevState) => ({ ...prevState, toLocation }));
                        }}
                    />
                </div>
            </div>
        </>
    );

    return (
        <ExportModal
            tooltip="outboundOrderBook.transportAssignments.export.tooltip"
            modalTitle="outboundOrderBook.transportAssignments.export.title"
            filename={FALL_BACK_FILE_NAME}
            getExport={() =>
                getExcel({
                    loadingLocation: localFilters.fromLocation,
                    unloadingLocation: localFilters.toLocation,
                    statuses: getStatusesForCategory(localFilters.tableCategories),
                    archived: getArchived(localFilters.tableCategories),
                    orderIssuedAtStart: getValidDate(localFilters.startDate),
                    orderIssuedAtEnd: getValidDate(localFilters.endDate),
                })
            }
            body={body}
            resetFilters={resetFilters}
            isDisabled={
                localFilters.tableCategories.length === 0 ||
                hasTimeRangeOrderError ||
                hasStartDateTimeError ||
                hasEndDateTimeError
            }
        />
    );
};
